import React from "react"
import { makeStyles } from "tss-react/mui"
import { Button, CircularProgress, Typography } from "@mui/material"

const useStyles = makeStyles()((theme) => ({
  // Full-screen scrim that blocks interaction with the chat underneath while
  // we attempt to reconnect. Sits above everything (the chat shell stays
  // mounted behind it, preserving messages + UI state).
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: theme.zIndex.modal + 10,
    backgroundColor: "rgba(15, 17, 21, 0.72)",
    backdropFilter: "blur(2px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  card: {
    width: 360,
    maxWidth: "90vw",
    backgroundColor: theme.palette.background.paper,
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
    textAlign: "center",
    boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
  },
  title: {
    fontWeight: 700,
    fontSize: "1.05rem",
    color: theme.palette.text.primary,
  },
  detail: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  },
  spinnerRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minHeight: 40,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    width: "100%",
    justifyContent: "center",
  },
  leaveButton: {
    color: theme.palette.text.secondary,
    borderColor: "rgba(255,255,255,0.2)",
    "&:hover": { borderColor: "rgba(255,255,255,0.4)" },
  },
  retryButton: {
    backgroundColor: theme.palette.primary.main,
    color: "#000",
    fontWeight: 600,
    "&:hover": { backgroundColor: theme.palette.primary.dark },
  },
}))

/**
 * Reconnection modal. Driven by App's reconnect state machine.
 *
 * Props:
 *   reconnect  - { attempt, phase } | null. phase: 'waiting' | 'connecting' | 'failed'
 *   maxAttempts - total scheduled attempts (for the "N of X" label)
 *   maintenance - true when the drop is a known server restart (deploy); shows
 *                 calm "updating, back in a moment" copy instead of an alarming
 *                 "connection lost".
 *   onRetry    - manual retry (only meaningful in 'failed')
 *   onLeave    - abandon retries and return to the instance auth home
 */
const ReconnectModal = ({ reconnect, maxAttempts, maintenance = false, onRetry, onLeave }) => {
  const { classes } = useStyles()
  if (!reconnect) return null

  const { attempt, phase } = reconnect
  const failed = phase === "failed"

  // Title: a known deploy reads as routine maintenance until/unless retries are
  // exhausted (at which point it's a real problem regardless of cause).
  const title = failed
    ? "Unable to reconnect"
    : maintenance
    ? "Updating…"
    : "Connection lost"

  return (
    <div className={classes.overlay}>
      <div className={classes.card}>
        <Typography className={classes.title}>{title}</Typography>

        {failed ? (
          <Typography className={classes.detail}>
            We couldn't reconnect after {maxAttempts} attempts. You can try again
            or return to the home screen.
          </Typography>
        ) : (
          <>
            <div className={classes.spinnerRow}>
              <CircularProgress size={22} thickness={5} />
              <Typography className={classes.detail}>
                {maintenance
                  ? "The chat is updating — back in a moment…"
                  : phase === "connecting"
                  ? `Reconnecting… (attempt ${attempt} of ${maxAttempts})`
                  : `Reconnection attempt ${attempt} of ${maxAttempts}…`}
              </Typography>
            </div>
          </>
        )}

        <div className={classes.actions}>
          {failed && (
            <Button
              variant="contained"
              className={classes.retryButton}
              onClick={onRetry}
              disableElevation
            >
              Try again
            </Button>
          )}
          <Button
            variant="outlined"
            className={classes.leaveButton}
            onClick={onLeave}
          >
            Leave
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ReconnectModal
